import { hasPendingEvents } from 'common/checkout-utils';
import { error, ValidationError } from 'common/errors';
import {
  getRememberMeType,
  pushAcceptedTnc,
  pushCheckoutResponse,
  pushCheckoutRequest,
  pushVisaRiskStepUp,
  pushErrors
} from 'common/gamp';
import { trackError, trackEvent } from 'common/observability';
import { propertyIsDefined } from 'common/type-guards';
import { isEmail, isMobileFormatE164, isValidNumber } from 'common/validators';

import checkoutIntegratedEnrollCardLegacy from 'communicator/external/checkout/integrated/legacy/enroll-card-legacy';
import identityLookup from 'communicator/external/identity-lookup/identity-lookup';
import initiateIdentityValidation from 'communicator/external/initiate-identity-validation/initiate-identity-validation';

import { reset, saveUser } from 'communicator/store';
import {
  getAccessToken,
  getCheckoutEnrollPayload,
  getComplianceResource,
  getFullAccessToken,
  getIsFeatureToggleEnabled,
  getSessionId,
  getTncEntryInfo,
  getTncStatus,
  getUser
} from 'communicator/store/selectors';
import { fetchCheckoutEnroll } from 'communicator/utils/api/checkout';
import { acceptTnc } from 'communicator/utils/api/tnc';
import { getCheckoutErrorMessage } from 'communicator/utils/checkout-error';
import { IDENTITY_VALIDATION_REQUIRED } from 'communicator/utils/error-enum-reason';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import unbind from 'communicator/utils/unbind';
import { RiskDecision } from 'types/api/intent';
import { AuthenticationMethod } from 'types/emvco';
import { RememberMeType } from 'types/gamp';
import { RequiredField } from 'types/utility';
import { OTPValidationConfig, OTPValidationWindowResolution } from 'types/validate-otp';
import {
  CheckoutCommunicatorPayload,
  CheckoutData,
  CheckoutIntegratedPayload
} from 'types/visa-sdk/checkout';

export default async function checkoutIntegratedEnrollCard(
  checkoutData: RequiredField<CheckoutData, 'encryptedCard'>,
  launchCvv: () => void,
  launchDcf: () => void,
  launchSRCBankApp: (authenticationMethod: AuthenticationMethod) => void,
  launchSRCIssuerOtp: (authenticationMethod: AuthenticationMethod) => void,
  launchOtpValidation: (config: OTPValidationConfig) => Promise<OTPValidationWindowResolution>
): Promise<CheckoutCommunicatorPayload | ValidationError> {
  const endMarker = getFullAccessToken()
    ? 'CHECKOUT_INTEGRATED_ENROLL_CARD_RETURNING'
    : 'CHECKOUT_INTEGRATED_ENROLL_CARD_NEW';

  if (!propertyIsDefined(checkoutData, 'consumer')) {
    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason: 'CARD_ADD_FAILED'
    });

    pushErrors('CARD_ADD_FAILED', 'Missing checkoutData.consumer');
    trackError('checkout-integrated-enroll-card:failure', {
      reason: 'Missing checkoutData.consumer'
    });

    return error('CARD_ADD_FAILED', 'Missing checkoutData.consumer');
  }

  const { consumerIdentity } = checkoutData.consumer;

  if (consumerIdentity) {
    const { mobileNumber, emailAddress = '' } = checkoutData.consumer;
    const isEmailIdentity = consumerIdentity.identityType === 'EMAIL_ADDRESS';

    if ((isEmailIdentity && !mobileNumber?.phoneNumber) || (!isEmailIdentity && !emailAddress)) {
      markEnd(endMarker, {
        doFlush: true,
        failure: true,
        reason: 'INVALID_PARAMETER'
      });

      const missingField = isEmailIdentity
        ? 'consumer.mobileNumber.phoneNumber'
        : 'consumer.emailAddress';

      pushErrors('CARD_ADD_FAILED: INVALID_PARAMETER', `${missingField} is required`);
      trackError('checkout-integrated-enroll-card:failure', {
        reason: `${missingField} is required`
      });

      return error('INVALID_PARAMETER', `${missingField} is required`);
    }

    const finalMobileNumber = `${mobileNumber?.countryCode || ''}${mobileNumber?.phoneNumber.replace(/^\+/, '') || ''}`;

    const isInvalidMobileNumber =
      getIsFeatureToggleEnabled('enableMobileAsId') &&
      isEmailIdentity &&
      (!isMobileFormatE164(finalMobileNumber) || !isValidNumber(finalMobileNumber));

    const isInvalidEmailAddress = !isEmailIdentity && !isEmail(emailAddress);

    if (isInvalidMobileNumber || isInvalidEmailAddress) {
      markEnd(endMarker, {
        doFlush: true,
        failure: true,
        reason: 'ID_FORMAT_UNSUPPORTED'
      });

      const errorMsg = isInvalidMobileNumber
        ? 'Invalid format: consumer.mobileNumber'
        : 'Invalid format: consumer.emailAddress';

      pushErrors('CARD_ADD_FAILED: ID_FORMAT_UNSUPPORTED', errorMsg);
      trackError('checkout-integrated-enroll-card:failure', {
        err: 'CARD_ADD_FAILED: ID_FORMAT_UNSUPPORTED',
        reason: errorMsg
      });

      return error('ID_FORMAT_UNSUPPORTED', errorMsg);
    }
  }

  const user = getUser();

  if (
    !getIsFeatureToggleEnabled('orchestratorCheckoutEnrollApi') ||
    (user?.isRecognizedUser && !getIsFeatureToggleEnabled('orchestratorCheckoutEnrollRecognized'))
  ) {
    return checkoutIntegratedEnrollCardLegacy(
      checkoutData,
      launchSRCIssuerOtp,
      launchSRCBankApp,
      launchCvv,
      launchDcf
    );
  }

  if (getTncStatus() === 'not_accepted') {
    const tncError = error(
      'CARD_ADD_FAILED',
      'One or more required parameter missing, please check missing fields'
    );

    if (!getComplianceResource('TERMS_AND_CONDITIONS')) {
      markEnd(endMarker, {
        doFlush: true,
        failure: true,
        reason: 'CARD_ADD_FAILED'
      });

      pushErrors('CARD_ADD_FAILED', 'Missing compliance resource: TERMS_AND_CONDITIONS');
      trackError('checkout-integrated-enroll-card:failure', {
        reason: 'Missing compliance resource: TERMS_AND_CONDITIONS'
      });

      return tncError;
    }

    const accessTokenState = getAccessToken();
    const tncEntryInfo = getTncEntryInfo();
    markStart('checkout_integrated_accept_tnc');
    const tncResponse = await acceptTnc({ accessToken: accessTokenState?.token, tncEntryInfo });
    markEnd('checkout_integrated_accept_tnc');

    if (!tncResponse.ok) {
      markEnd(endMarker, {
        doFlush: true,
        failure: true,
        reason: 'CARD_ADD_FAILED'
      });

      pushErrors(
        'CARD_ADD_FAILED: ACCEPT_TNC',
        'Unable to accept TNC, please check missing fields'
      );

      trackError('checkout-integrated-enroll-card:failure', {
        reason: 'Unable to accept TNC, please check missing fields',
        status: tncResponse.status
      });

      return tncError;
    }

    pushAcceptedTnc();

    saveUser({
      accessToken: {
        expiresIn: tncResponse.data.expires_in,
        token: tncResponse.data.access_token,
        isPartial: false
      },
      tnc: {
        status: 'accepted',
        tncEntryInfo
      }
    });
  }

  const uctpAddCard = getIsFeatureToggleEnabled('uctpAddCard');
  const rememberMe: RememberMeType = getRememberMeType(checkoutData.complianceSettings);

  pushCheckoutRequest(true, rememberMe);

  const checkoutResponse = await fetchCheckoutEnroll({
    accessToken: getFullAccessToken(),
    payload: await getCheckoutEnrollPayload(),
    sessionId: getSessionId(),
    uctpAddCard: uctpAddCard || false,
    vsbFlow: checkoutData.vsbFlow || false
  });

  function processCheckoutResponse(
    checkoutResponse: {
      ok: boolean;
      enrollFlow?: string | null;
      data: {
        cardBrand?: boolean;
        riskDecision?: RiskDecision | null;
        checkoutResponse: CheckoutIntegratedPayload['checkoutResponse'];
        recognitionToken?: string;
      };
    },
    checkoutData: RequiredField<CheckoutData, 'encryptedCard'>,
    endMarker: string,
    uctpAddCard: boolean,
    rememberMe: RememberMeType
  ): CheckoutCommunicatorPayload | ValidationError {
    if (uctpAddCard && checkoutResponse.enrollFlow === 'UCTP' && checkoutResponse.data.cardBrand) {
      // @ts-expect-error - UCTP response is incompatible with EMVCo, returning OrchestratorCheckoutEnrollResponse, not CheckoutCommunicatorPayload
      return checkoutResponse;
    }

    const stime = Date.now();
    switch (checkoutResponse.data.riskDecision) {
      case 'REQUIRED3DS':
      case 'REQUIRED_BIOMETRIC':
      case 'REQUIRED_CVV':
      case 'REQUIRED_ISSUER_CTF':
      case 'REQUIRED_VISA_OTP':
        markEnd(endMarker, { doFlush: true, failure: true });
        pushVisaRiskStepUp(
          true,
          rememberMe,
          undefined,
          checkoutResponse.data.riskDecision,
          Date.now() - stime
        );

        pushErrors(
          'CARD_ADD_FAILED: RISK_DECISION',
          `Risk decision: ${checkoutResponse.data.riskDecision}`
        );

        trackError('checkout-integrated-enroll-card:failure', {
          reason: `Risk decision: ${checkoutResponse.data.riskDecision}`
        });

        return error('RISK_DECISION', checkoutResponse.data.riskDecision);
    }

    markEnd(endMarker, { doFlush: true });
    pushCheckoutResponse(
      checkoutResponse.ok,
      true,
      rememberMe,
      undefined,
      undefined,
      Date.now() - stime
    );

    trackEvent('checkout-integrated-enroll-card:success', {
      riskDecision: checkoutResponse.data.riskDecision
    });

    const unbindAppInstance = !user?.isRecognizedUser && !getComplianceResource('REMEMBER_ME');

    if (!hasPendingEvents(checkoutResponse.data.checkoutResponse)) {
      if (unbindAppInstance) {
        unbind({ accessToken: getFullAccessToken() });
      }

      reset();
    }

    const checkoutPayload: CheckoutIntegratedPayload = {
      checkoutResponse: checkoutResponse.data.checkoutResponse,
      ...checkoutResponse.data.recognitionToken && { recognitionToken: checkoutResponse.data.recognitionToken },
      dcfActionCode: 'COMPLETE',
      unbindAppInstance
    };

    if (checkoutData.idToken) {
      checkoutPayload.idToken = checkoutData.idToken;
    }

    return {
      payload: checkoutPayload
    };
  }

  function sendError(reason: string, message: string, status: number) {
    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason
    });

    pushErrors(reason, message);
    trackError('checkout-integrated-enroll-card:failure', {
      reason,
      message,
      status
    });

    return error(reason, message);
  }

  function getIdentityValue() {
    const consumerIdentity = checkoutData.consumer && checkoutData.consumer.consumerIdentity;

    if (!consumerIdentity?.identityValue) {
      return '';
    }

    const isMobilePhone = consumerIdentity.identityType === 'MOBILE_PHONE_NUMBER';

    if (isMobilePhone) {
      return consumerIdentity.identityValue.replace(/^\+/, '');
    }

    return consumerIdentity.identityValue;
  }

  if (checkoutResponse.ok) {
    return processCheckoutResponse(
      {
        ...checkoutResponse,
        data: {
          ...checkoutResponse.data,
          riskDecision: checkoutResponse.data.riskDecision as RiskDecision | null | undefined
        }
      },
      checkoutData,
      endMarker,
      uctpAddCard || false,
      rememberMe
    );
  }

  const { message, reason } = getCheckoutErrorMessage(checkoutResponse);
  const isInlineIdentityValidationEnabled = getIsFeatureToggleEnabled(
    'enablePerformInlineIdentityValidation'
  );

  if (getIsFeatureToggleEnabled('debuggingEnabled')) {
    console.log(
      `%c🎯 enablePerformInlineIdentityValidation - ${isInlineIdentityValidationEnabled ? '✅ ENABLED' : '❌ DISABLED'}`,
      `background: ${isInlineIdentityValidationEnabled ? '#4CAF50' : '#F44336'}; color: white; padding: 10px; border-radius: 5px; font-weight: bold; font-size: 14px;`
    );
  }

  if (reason === IDENTITY_VALIDATION_REQUIRED && isInlineIdentityValidationEnabled) {
    if (!getAccessToken()) {
      const { consumerIdentity } = checkoutData.consumer;
      const identityType = consumerIdentity?.identityType ?? 'EMAIL_ADDRESS';
      const identityValue = getIdentityValue();
      const identityProvider = consumerIdentity?.identityProvider ?? 'SRC';

      const lookupData = {
        data: {
          consumerIdentity: {
            identityProvider,
            identityType,
            identityValue
          }
        },
        source: checkoutData.windowRef || window,
        origin: window.origin
      };

      const lookup = await identityLookup(lookupData);

      if ('error' in lookup) {
        return lookup;
      }
    }

    const initiate = await initiateIdentityValidation();

    if ('error' in initiate) {
      return initiate;
    }

    let maskedValidationChannel = '';
    const hasMaskedValidationChannel =
      typeof initiate === 'object' && 'maskedValidationChannel' in initiate;

    const isValidMaskedChannel =
      hasMaskedValidationChannel &&
      initiate.maskedValidationChannel &&
      typeof initiate.maskedValidationChannel === 'string';

    if (isValidMaskedChannel) {
      maskedValidationChannel = initiate.maskedValidationChannel;
    }

    const hasValidProfileLocale =
      typeof initiate === 'object' &&
      'profileLocale' in initiate &&
      typeof initiate.profileLocale === 'string';

    const profileLocale: string = (hasValidProfileLocale && initiate.profileLocale) || 'en-US';

    const otpConfig: OTPValidationConfig = {
      locale: profileLocale,
      maskedData: maskedValidationChannel,
      parentDomain: window.origin
    };

    const otpValidationWindowResponse = await launchOtpValidation(otpConfig);

    switch (otpValidationWindowResponse.status) {
      case 'SUCCESS': {
        const secondCheckoutResponse = await fetchCheckoutEnroll({
          accessToken: getFullAccessToken(),
          payload: await getCheckoutEnrollPayload(),
          sessionId: getSessionId(),
          uctpAddCard: uctpAddCard || false,
          vsbFlow: checkoutData.vsbFlow || false
        });

        if (!secondCheckoutResponse.ok) {
          const { reason, message } = getCheckoutErrorMessage(secondCheckoutResponse);
          return sendError(reason, message, secondCheckoutResponse.status || 500);
        }

        const checkoutPayload = {
          ...checkoutResponse,
          data: {
            ...secondCheckoutResponse.data,
            riskDecision: secondCheckoutResponse.data.riskDecision as
              | RiskDecision
              | null
              | undefined
          }
        };

        otpValidationWindowResponse.cleanup();

        return processCheckoutResponse(
          checkoutPayload,
          checkoutData,
          endMarker,
          uctpAddCard || false,
          rememberMe
        );
      }
      case 'ACCT_INACCESSIBLE':
        return sendError(
          'ACCT_INACCESSIBLE',
          'Too many failed attempts, please try again later',
          checkoutResponse.status || 500
        );
      default:
        return sendError(reason, message, checkoutResponse.status || 500);
    }
  }

  return sendError(reason, message, checkoutResponse.status || 500);
}
